import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Tooltip,
  IconButton,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ExploreIcon from '@mui/icons-material/Explore';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatIcon from '@mui/icons-material/Chat';
import ShareIcon from '@mui/icons-material/Share';
import ForumIcon from '@mui/icons-material/Forum';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import TuneIcon from '@mui/icons-material/Tune';
import FeedbackIcon from '@mui/icons-material/Feedback';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { SAGE, CREAM, WARM } from '../theme';
import useAuth from '../hooks/useAuth';

const SIDEBAR_WIDTH = 64;

const navItems = [
  { icon: DashboardIcon, label: 'Dashboard', path: '/' },
  // Companies + Roles live under /explore — they're "learn the landscape"
  // surfaces. Specific open postings will eventually live at /jobs (the
  // generic JobsBrowserPage, hidden for now and unhidden when bulk-apply
  // ships).
  { icon: ExploreIcon, label: 'Explore Roles', path: '/explore/roles' },
  { icon: BusinessIcon, label: 'Companies', path: '/explore/companies' },
  { icon: DescriptionIcon, label: 'Applications', path: '/applications', adminOnly: true },
  { icon: ChatIcon, label: 'Conversations', path: '/profile/enhance', adminOnly: true },
  { icon: ForumIcon, label: 'Interview', path: '/interview', adminOnly: true },
  { icon: ShareIcon, label: 'Share Link', path: '/admin/shares', adminOnly: true },
  { icon: PersonIcon, label: 'Profile', path: '/profile' },
  { icon: TimelineIcon, label: 'Timeline', path: '/profile/timeline', adminOnly: true },
  { icon: TuneIcon, label: 'Preferences', path: '/preferences', adminOnly: true },
];

const tooltipSlotProps = {
  tooltip: {
    sx: {
      bgcolor: SAGE[800],
      fontFamily: "'Outfit', sans-serif",
      fontSize: '0.75rem',
    },
  },
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { username, isLoggedIn, isAdmin, handleLogin, handleLogout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        bgcolor: CREAM[50],
        borderRight: `2px solid ${SAGE[200]}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 2,
        zIndex: 1200,
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: SAGE[500],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 700,
          fontSize: '1.1rem',
          mb: 3,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      >
        O
      </Box>

      {/* Nav Icons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        {[
          ...navItems.filter((item) => !item.adminOnly || isAdmin),
          ...(isAdmin ? [{ icon: AdminPanelSettingsIcon, label: 'Admin', path: '/admin' }] : []),
        ].map(({ icon: Icon, label, path, stub, badge }) => {
          const active = isActive(path);
          return (
            <Tooltip key={path} title={label} placement="right" slotProps={tooltipSlotProps}>
              <IconButton
                onClick={() => !stub && navigate(path)}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  color: active ? SAGE[700] : SAGE[400],
                  bgcolor: active ? SAGE[100] : 'transparent',
                  '&:hover': {
                    bgcolor: SAGE[100],
                    color: SAGE[700],
                  },
                  opacity: stub ? 0.5 : 1,
                  cursor: stub ? 'default' : 'pointer',
                }}
              >
                {badge ? (
                  <Badge
                    badgeContent={badge}
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: WARM.rose,
                        color: '#fff',
                        fontSize: '0.65rem',
                        minWidth: 16,
                        height: 16,
                      },
                    }}
                  >
                    <Icon fontSize="small" />
                  </Badge>
                ) : (
                  <Icon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          );
        })}
      </Box>

      {/* Bottom: Feedback + Avatar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Feedback" placement="right" slotProps={tooltipSlotProps}>
          <IconButton sx={{ color: SAGE[400], '&:hover': { color: SAGE[700] } }}>
            <FeedbackIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={isLoggedIn ? username : 'Login'} placement="right" slotProps={tooltipSlotProps}>
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ p: 0.5 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: isLoggedIn ? SAGE[500] : SAGE[200],
                fontSize: '0.85rem',
                fontFamily: "'Outfit', sans-serif",
              }}
            >
              {isLoggedIn ? username.charAt(0).toUpperCase() : '?'}
            </Avatar>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
        >
          {isLoggedIn ? (
            <MenuItem onClick={() => { setAnchorEl(null); handleLogout(); }}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={() => { setAnchorEl(null); handleLogin(); }}>
              <ListItemIcon><LoginIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Login</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </Box>
    </Box>
  );
};

export { SIDEBAR_WIDTH };
export default Sidebar;
